import { Component, OnInit, ChangeDetectionStrategy, Input, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { CustomAlertService } from 'src/app/_components/custom-alert/custom-alert.service';
import { NivelCriterio } from 'src/app/_models/meta';
import { Autoevaluacion } from '../autoevaluacion';
import { AutoevaluacionService } from '../autoevaluacion.service';

@Component({
  selector: 'app-autoevaluacion-criterio-group',
  standalone: false,
  templateUrl: './autoevaluacion-criterio-group.component.html',
  styleUrls: ['./autoevaluacion-criterio-group.component.scss', '../autoevaluacion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoevaluacionCriterioGroupComponent implements OnInit, OnDestroy {

  @Input()
  alumnoId: number;
  @Input()
  unidadEventoMateriaId: number;
  @Input()
  autoevaluacion$: Observable<Autoevaluacion>;
  @Input()
  autoevaluacion: Autoevaluacion;

  @Input()
  nivel: NivelCriterio;

  @Input()
  evaluado$: Observable<boolean>;

  @Input()
  criterioEvaluacionId: number;

  anteriorId: number;

  radioFormSubscription: Subscription;
  autoevaluacionSubscription: Subscription;

  expandSubject = new BehaviorSubject(false);
  expand$ = this.expandSubject.asObservable();

  expanded$: Observable<boolean>;

  radioForm = new UntypedFormGroup({
    radio: new UntypedFormControl(null),
    save: new UntypedFormControl(false),
  });

  constructor(private customAlertService: CustomAlertService,
    private snackBar: MatSnackBar,
    private autoevaluacionService: AutoevaluacionService) { }

  ngOnInit(): void {
    this.radioFormSubscription = this.mecanismoDeGuardado(this.radioForm.valueChanges).subscribe(
      res => {

      },
      err => {
       this.customAlertService.setCustomMessage({
         tipo: 'error',
         texto: `No ha sido posible guardar la evaluación. <br> Código de error: ${err.statusText}`
       });
      }
    );
    this.autoevaluacionSubscription = this.actualizarRadioForm(this.autoevaluacion$).subscribe();

    this.expanded$ = combineLatest([this.expand$, this.autoevaluacion$]).pipe(
      map(([expand, autoevaluacion]) => {
        return !expand &&
        (autoevaluacion.eventoMateriaAlumnoConfig.completoAutoevaluacion && autoevaluacion.criteriosAutoevaluacion.length === 0);
      })
    );
  }

  mecanismoDeGuardado(radioFormObs$: Observable<{radio: number, save: boolean}>): Observable<any> {
    return radioFormObs$.pipe(
      mergeMap(data => {
        if (data.save === true) {
          if (data.radio === null) {
            if (this.anteriorId) {
              const anteriorIdTemp = this.anteriorId;
              this.anteriorId = 0;
              this.autoevaluacionService.cambiosEmit(this.alumnoId, anteriorIdTemp, this.unidadEventoMateriaId, 'delete');
              return this.autoevaluacionService.deleteEvaluacion(anteriorIdTemp,
                this.unidadEventoMateriaId, this.alumnoId).pipe(
                catchError(err => {
                  this.anteriorId = anteriorIdTemp;
                  this.autoevaluacionService.cambiosEmit(this.alumnoId, anteriorIdTemp,
                    this.unidadEventoMateriaId, 'create');
                  this.customAlertService.setCustomMessage({
                    tipo: 'error',
                    texto: `No ha sido posible guardar la autoevaluación. <br> Código de error: ${err.statusText}`
                  });
                  return of(true);
                })
              );
            } else {
              return of(true);
            }
          } else {
            const anteriorIdTemp = this.anteriorId;
            this.anteriorId = data.radio;
            const objetoEvaluacion = {
              criterioId: data.radio,
              alumnoId: this.alumnoId,
              unidadEventoMateriaId: this.unidadEventoMateriaId
            };
            this.autoevaluacionService.cambiosEmit(this.alumnoId, anteriorIdTemp, this.unidadEventoMateriaId, 'delete');
            this.autoevaluacionService.cambiosEmit(this.alumnoId, data.radio, this.unidadEventoMateriaId, 'create');
            return this.autoevaluacionService.setEstadoEvaluacionCriterio(objetoEvaluacion).pipe(
              catchError(err => {
                this.autoevaluacionService.cambiosEmit(this.alumnoId, data.radio, this.unidadEventoMateriaId, 'delete');
                if (this.anteriorId) {
                  this.autoevaluacionService.cambiosEmit(this.alumnoId, anteriorIdTemp,
                    this.unidadEventoMateriaId, 'create');
                }
                this.customAlertService.setCustomMessage({
                  tipo: 'error',
                  texto: `No ha sido posible guardar la autoevaluación. <br> Código de error: ${err.statusText}`
                });
                return of(true);
              })
            );
          }
        } else {
          return of(true);
        }
      }),
      tap((data => {
        if (data !== true) {
          const respuesta = data ? `La autoevaluación fue guardada exitosamente.` : `La autoevaluación fue eliminada exitosamente.`;
          this.snackBar.open(respuesta, null, {duration: 2500});
        }
      }))
   );
  }

  actualizarRadioForm(autoevaluacion$: Observable<Autoevaluacion>) {
    return autoevaluacion$.pipe(
      tap(data => {
        this.radioForm.setValue({radio: null, save: false});
        data.criteriosAutoevaluacion.forEach(criterio => {
          this.nivel.criterios.forEach(criterioNivel => {
            if (criterioNivel.id === criterio.criterioId) {
              this.anteriorId = criterio.criterioId;
              this.radioForm.setValue( {radio: criterio.criterioId, save: false});
            }
          });
        });
        if (data.criterios) {
          data.criterios.forEach(criterio => {
            this.nivel.criterios.forEach(criterioNivel => {
              if (criterioNivel.id === criterio.criterioId) {
                this.criterioEvaluacionId = criterio.criterioId;
              }
            });
          });
        }
      })
    );
  }

  expand() {
    this.expandSubject.next(true);
  }

  ngOnDestroy(): void {
    if (this.autoevaluacionSubscription) {
      this.autoevaluacionSubscription.unsubscribe();
    }
    if (this.radioFormSubscription) {
      setTimeout(() => {
        this.radioFormSubscription.unsubscribe();
      }, 100);
    }
  }

}
